import { type ReactElement, type ReactNode, useRef, useState } from 'react';
import {
  Box,
  Combobox,
  Group,
  Input,
  InputBase,
  NumberInput,
  type NumberInputProps,
  Select,
  type SelectProps,
  Text,
  Textarea,
  type TextareaProps,
  TextInput,
  type TextInputProps,
  Tooltip,
  useCombobox,
} from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import { IconAlertTriangle } from '@tabler/icons-react';

import {
  AVV_BY_CODE,
  AVV_LIMIT,
  type AvvItem,
  avvGroupRuns,
  avvHitLabel,
  avvVisibleItems,
  type WasteCodeUsage,
} from './avvCatalog.js';
import { BelowLimitToggle } from './BelowLimitToggle.js';
import { toDecimalString } from './decimalString.js';
import { useRegisterFieldTab } from './fieldTabRegistry.js';
import { useFieldWarning } from './fieldWarnings.js';
import { PreviousValueForField } from './PreviousValueLine.js';
import { unitSuffix, withUnit } from './unitLabel.js';
import avvClasses from './avvSelect.module.css';

/**
 * Reusable form-field wrappers that bind a Mantine input to a `useForm`
 * (uncontrolled) field AND render validation errors the project way:
 * red fill + red border + a warning icon on the field, and the message in a
 * tooltip above it, shown on hover/focus only.
 *
 * Single source of truth for field errors is the backend (see
 * /api/reports/validate); these components only display `form.errors[field]`.
 *
 * Seit Plan 20-07 gibt es zwei Meldungsstufen (D-09): rot blockiert, gelb nicht.
 * Beide sind strukturgleich -- dieselbe Fuellung/Rahmen/Icon/Tooltip-Mechanik, nur
 * mit anderer Farbe. Der Unterschied liegt allein in der Farbe und darin, dass die
 * gelbe Stufe Mantines `error`-Prop NICHT setzt: das Feld bleibt vollstaendig
 * bedienbar (T-20-07-04).
 *
 * Seit Plan 20-16 holen sich diese Komponenten BEIDE Zusatzangaben selbst, statt sie vom
 * jeweiligen Formular durchgereicht zu bekommen:
 * - den Hinweis zu ihrem Feld aus `FieldWarningsContext` (`useFieldWarning`)
 * - die Zugehoerigkeit zu einem Reiter, gemeldet an `FieldTabRegistryContext`
 *   (`useRegisterFieldTab`)
 * Beides haengt ohnehin nur am Feldnamen, den die Komponente als Prop bekommt. Dadurch
 * greifen gelbe Markierung und Reiterpunkt in ALLEN fuenf Formularen, ohne dass eines
 * davon Feldlisten pflegen oder Props durchreichen muss. Der `warning`-Prop bleibt als
 * ausdruecklicher Vorrang bestehen -- Abschnitte mit eigener Hinweisdarstellung (etwa die
 * berechneten Zellen der Unfallmatrix) reichen ihn weiterhin selbst.
 */

// Loose form type so any typed useForm (e.g. AccidentDataFormData) can be passed
// to these shared wrappers without invariance friction.
// Exported (Plan 27.1-05) so BelowLimitToggle.tsx -- eine eigenstaendige Blattkomponente
// ausserhalb dieses Moduls -- denselben lose getypten Formular-Typ verwenden kann.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyForm = UseFormReturnType<any>;

// Colours map to the Mantine `red` scale: red.2 #FFC9C9, red.3 #FFA8A8, red.9 #C92A2A.
const ERROR_FILL = 'var(--mantine-color-red-2)';
const ERROR_LINE = 'var(--mantine-color-red-9)';
const TOOLTIP_FILL = 'var(--mantine-color-red-3)';

// Gelbe Schwester der roten Stufe (20-07, D-09), auf der Mantine-`yellow`-Skala an
// denselben Positionen: yellow.2 Fuellung, yellow.3 Tooltip, yellow.9 Rahmen/Text/Icon.
const WARNING_FILL = 'var(--mantine-color-yellow-2)';
const WARNING_LINE = 'var(--mantine-color-yellow-9)';
const WARNING_TOOLTIP_FILL = 'var(--mantine-color-yellow-3)';

/** Farbgebung einer Meldungsstufe -- der einzige Unterschied zwischen Rot und Gelb. */
interface Severity {
  fill: string;
  line: string;
  tooltipFill: string;
}

const ERROR_SEVERITY: Severity = { fill: ERROR_FILL, line: ERROR_LINE, tooltipFill: TOOLTIP_FILL };
const WARNING_SEVERITY: Severity = {
  fill: WARNING_FILL,
  line: WARNING_LINE,
  tooltipFill: WARNING_TOOLTIP_FILL,
};

function messageInputStyles(severity: Severity | undefined, align?: 'left' | 'right') {
  const input: React.CSSProperties = {};
  if (align) {
    input.textAlign = align;
  }
  if (severity) {
    input.backgroundColor = severity.fill;
    input.borderColor = severity.line;
    input.color = severity.line;
  }
  return { input };
}

/** Nur der `styles`-Objekt-Fall wird gemergt; die Funktions-Variante von Mantine kommt hier nicht vor. */
type SlotStyles = Record<string, React.CSSProperties>;

/**
 * Fuehrt die Fehler-/Warnfaerbung mit einem von aussen uebergebenen `styles` zusammen.
 *
 * Ohne diesen Merge gewinnt schlicht das zuletzt gesetzte `styles`-Prop: die Komponenten setzen
 * `styles={messageInputStyles(...)}` nach `{...rest}`, wodurch ein aeusseres `styles` spurlos
 * verschwand. Genau daran ist die `borderLeft`-Randlinie der „davon"-Felder in `FormularCForm`
 * gescheitert -- sie stand im Code, kam aber nie im DOM an.
 *
 * Der Merge laeuft je Slot (`root`, `input`, ...), nicht flach ueber das Objekt: ein aeusseres
 * `styles.root` darf die Fehlerfaerbung auf `input` nicht verdraengen und umgekehrt. Innerhalb
 * eines Slots gewinnt die Faerbung, damit ein Feldfehler immer sichtbar bleibt.
 */
function mergeInputStyles(
  own: SlotStyles,
  incoming: unknown
): SlotStyles {
  if (!incoming || typeof incoming !== 'object') return own;
  const outer = incoming as SlotStyles;
  const merged: SlotStyles = { ...outer };
  for (const [slot, ownSlotStyles] of Object.entries(own)) {
    merged[slot] = { ...(outer[slot] ?? {}), ...ownSlotStyles };
  }
  return merged;
}

function messageIcon(severity: Severity | undefined) {
  return severity ? <IconAlertTriangle size={16} color={severity.line} /> : undefined;
}

/**
 * Rot hat Vorrang vor Gelb: liegt auf demselben Feld ein Fehler an, wird der Hinweis
 * ignoriert. Der Fehler ist die staerkere Aussage ("so nicht speicherbar/einreichbar"),
 * der Hinweis nur eine Plausibilitaetsfrage -- beide gleichzeitig anzuzeigen ginge in
 * einem einzelnen Feld ohnehin nicht, und die gelbe Faerbung wuerde die rote entwerten.
 *
 * `isError` steuert allein Mantines `error`-Prop: bei reiner Warnung bleibt es `false`,
 * damit weder Formularabsenden noch Fokusverhalten beeinflusst werden (T-20-07-04).
 */
function fieldMessage(
  error: string | undefined,
  warning: string | undefined
): { message?: string; severity?: Severity; isError: boolean } {
  if (error) return { message: error, severity: ERROR_SEVERITY, isError: true };
  if (warning) return { message: warning, severity: WARNING_SEVERITY, isError: false };
  return { message: undefined, severity: undefined, isError: false };
}

/**
 * Der gemeinsame Kopf aller vier Feldkomponenten: Reiter-Anmeldung, Hinweis-Abo und die
 * Vorrangregel in einem Aufruf.
 *
 * Der uebergebene `warning` schlaegt den Hinweis aus dem Kontext -- ein Abschnitt, der seine
 * Hinweise selbst zuordnet, behaelt damit das letzte Wort. Nur wenn er nichts reicht, greift
 * die allgemeine, feldnamenbasierte Zuordnung.
 */
function useFieldMessage(
  form: AnyForm,
  field: string,
  warning: string | undefined,
  label?: string
): { message?: string; severity?: Severity; isError: boolean } {
  useRegisterFieldTab(field, label);
  const contextWarning = useFieldWarning(field);
  return fieldMessage(fieldError(form, field), warning ?? contextWarning);
}

/**
 * Beschriftung, die ein Feld an die Registry meldet (D-13): `fieldLabel` schlaegt `label`,
 * das wiederum `aria-label`. Nur Strings zaehlen -- ein ReactNode-Label hat keinen Klartext.
 */
function registryLabel(fieldLabel: unknown, label: unknown, ariaLabel: unknown): string | undefined {
  for (const candidate of [fieldLabel, label, ariaLabel]) {
    if (typeof candidate === 'string' && candidate.trim() !== '') return candidate;
  }
  return undefined;
}

/**
 * Wraps a field in a message tooltip above it. Visibility is fully controlled and
 * follows exactly two triggers: hovering the field or focusing it. A message that
 * newly appears does NOT open its bubble on its own.
 *
 * Hover/focus are tracked on the whole field wrapper, so hovering the warning
 * icon (a left section, outside the native input) opens it too.
 *
 * Warum keine Auto-Anzeige mehr (Plan 20-15): sie oeffnete pro Feld eine Sprechblase,
 * sobald dessen Meldung erschien oder ihren Text wechselte. Nach einem abgelehnten
 * Einreichen erscheinen die Meldungen aber ALLE gleichzeitig -- auf dem
 * Unfallstatistik-Reiter wurden 18 gleichzeitig offene Sprechblasen gezaehlt, bei
 * Feldbreiten von 252-279 px und einer Blasenbreite von 260 px ueberlappten drei Paare
 * messbar. Ursache ist das gleichzeitige Aufklappen, nicht die Breite; deshalb bleibt
 * `w={260}` unveraendert (siehe unten). Die Information geht nicht verloren: das Feld
 * bleibt rot bzw. gelb, der Reiter traegt seinen Punkt, und die Fehlerbox oben listet
 * alle Meldungen entdoppelt auf (Plan 20-14).
 *
 * `severity` ist der einzige Unterschied zwischen der roten und der gelben Stufe --
 * das Verhalten (Hover/Fokus, Anker, Breite) ist identisch.
 *
 * `below` (D-12) haengt den Vorwert als GESCHWISTER des Tooltips in dieselbe Box, nicht als
 * dessen zweites Kind: Mantines `Tooltip` erwartet genau ein Kind (deshalb ist `children`
 * bereits als `ReactElement` und nicht als `ReactNode` typisiert). Nebenwirkung aus D-20:
 * der Vorwert liegt damit innerhalb derselben Hover-/Fokusflaeche wie das Feld -- ein Hover
 * ueber ihm oeffnet die Sprechblase des Feldes mit, bewusst hingenommen, kein Fehler.
 */
function FieldMessageTooltip({
  message,
  severity = ERROR_SEVERITY,
  below,
  children,
}: {
  message?: string;
  severity?: Severity;
  below?: ReactNode;
  children: ReactElement;
}) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const opened = !!message && (hovered || focused);

  // The Box wraps the Tooltip (not the other way round) so the tooltip still
  // anchors to the input element itself — i.e. directly above the field — while
  // hover/focus is tracked across the whole field, including the warning icon.
  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <Tooltip
        label={message}
        opened={opened}
        // Fully controlled — we drive visibility from hover/focus only.
        events={{ hover: false, focus: false, touch: false }}
        multiline
        // Bleibt bei 260 px (20-15): es ist immer nur EINE Blase offen, also kann nichts
        // ueberlappen -- und schmaler zerlegte die ~120 Zeichen langen Hinweistexte nur in
        // hohe Tuerme.
        w={260}
        withArrow
        position="top-start"
        styles={{
          tooltip: { backgroundColor: severity.tooltipFill, color: severity.line, fontWeight: 500 },
          arrow: { backgroundColor: severity.tooltipFill },
        }}
      >
        {children}
      </Tooltip>
      {below}
    </Box>
  );
}

/**
 * Benannte gelbe Variante fuer Konsumenten AUSSERHALB dieser Datei -- etwa die
 * berechneten Zellen der Unfallstatistik-Matrix, die keine `ValidatedNumberInput`
 * sind, aber dieselbe Hinweis-Darstellung tragen sollen (K6/D-13).
 */
export function FieldWarningTooltip({
  warning,
  children,
}: {
  warning?: string;
  children: ReactElement;
}) {
  return (
    <FieldMessageTooltip message={warning} severity={WARNING_SEVERITY}>
      {children}
    </FieldMessageTooltip>
  );
}

/**
 * Gelbe Fuellung/Rahmen und gelbes Icon fuer dieselben externen Konsumenten wie
 * `FieldWarningTooltip` -- eine berechnete, readonly gerenderte Zelle braucht neben dem
 * Tooltip auch die Faerbung. Ohne diese beiden Exporte muesste sie ihre Farben aus
 * kopierten Konstanten bauen, und die Warnstufe waere nicht mehr an einer Stelle
 * definiert; die Farbkonstanten selbst bleiben deshalb modulprivat.
 *
 * Bewusst als Konstanten statt als Funktionen: die Fuellung ist zustandslos, und
 * `react-refresh/only-export-components` laesst Konstanten-Exporte zu
 * (`allowConstantExport`), zusaetzliche Funktions-Exporte dagegen nicht.
 */
export const WARNING_INPUT_STYLES = {
  backgroundColor: WARNING_FILL,
  borderColor: WARNING_LINE,
  color: WARNING_LINE,
} as const;

export const WARNING_ICON = <IconAlertTriangle size={16} color={WARNING_LINE} />;

/** Textfarbe der gelben Stufe -- fuer Hinweise ausserhalb eines Eingabefeldes (`FieldWarningNote`). */
export const WARNING_COLOR = WARNING_LINE;

function fieldError(form: AnyForm, field: string): string | undefined {
  const { error } = form.getInputProps(field);
  return typeof error === 'string' ? error : undefined;
}

// form.getValues() returns the actual nested values object (arrays stay arrays), so a dotted
// path like "inputMaterials.0.clinkerProduction" is NOT a literal top-level key — bracket access
// (values[field]) would silently return undefined for any nested/array field path. getPath walks
// the dot-separated segments the same way Mantine's own getInputProps/setFieldValue do internally.
function getPath(values: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc === null || acc === undefined) return undefined;
    return (acc as Record<string, unknown>)[key];
  }, values);
}

// In uncontrolled mode, setFieldValue bumps the field key (remounting the input,
// dropping focus) unless forceUpdate:false is passed — matching getInputProps.
// Exported so callers outside this module (e.g. ReportEditPage's notes-mirroring,
// EnvironmentalDataForm's Drehoefen bridge) can reuse the same guard.
export const NO_REMOUNT = { forceUpdate: false } as const;

/**
 * Vorrangregel fuer den `leftSection`-Slot von `ValidatedNumberInput` (D-08/D-20). Als reine
 * Funktion ausgelagert, damit die eigentliche Entscheidung -- der Umschalter ueberlebt den
 * Fehlerzustand -- ohne Rendering testbar ist (siehe `validatedFields.test.ts`).
 *
 * - `hasBelowLimitField=true` -> immer `'below-limit-toggle'`, unabhaengig von `hasMessage`.
 *   Das IST die D-20-Entscheidung: der Umschalter verdraengt das Fehlersymbol, nicht umgekehrt.
 * - `hasBelowLimitField=false` -> unveraendertes Verhalten aller bestehenden Verwender
 *   (`'message-icon'` bei Fehler/Hinweis, sonst `'passthrough'` fuer den generischen Prop).
 *
 * Verdrahtet in `ValidatedNumberInput` (unten). Exportiert, damit `validatedFields.test.ts` die
 * Regel ohne Rendering pruefen kann (siehe Kommentarkopf dort, warum kein Render-Test).
 */
export type LeftSectionKind = 'below-limit-toggle' | 'message-icon' | 'passthrough';

// react-refresh/only-export-components warnt vor Funktions-Exporten neben Komponenten-Exporten in
// derselben Datei -- hier bewusst in Kauf genommen: die Regel selbst (D-20) ist eine reine
// Funktion und muss ohne Rendering testbar bleiben (siehe Kommentar oben); eine Auslagerung in
// eine weitere Datei wuerde den Diff dieses Plans ueber die vier vorgesehenen Dateien hinaus
// erweitern (`validatedFields.tsx`, `validatedFields.test.ts`, `BelowLimitToggle.tsx`,
// `belowLimitToggle.module.css`).
// eslint-disable-next-line react-refresh/only-export-components
export function resolveLeftSectionKind(hasBelowLimitField: boolean, hasMessage: boolean): LeftSectionKind {
  if (hasBelowLimitField) return 'below-limit-toggle';
  return hasMessage ? 'message-icon' : 'passthrough';
}

// 28px (Sketch 011, Variante D: ein einzelner Kippknopf "<" statt zweier Umschaltknoepfe).
export const BELOW_LIMIT_TOGGLE_WIDTH = 28;

/**
 * Zusatz-Props fuer `NumberInput`, wenn ein Umschalter im `leftSection`-Slot sitzt (D-08 opt-in).
 * Ohne `belowLimitField` liefert dies ein leeres Objekt -- kein einziger zusaetzlicher Prop fuer
 * die >100 bestehenden Verwender. `leftSectionPointerEvents: 'all'` ist Pflicht: Mantines
 * Voreinstellung fuer den `leftSection`-Wrapper ist `pointer-events: none`, ohne die Ueberschreibung
 * waere der Umschalter sichtbar, aber nicht klickbar.
 *
 * Verdrahtet in `ValidatedNumberInput` (unten).
 */
// eslint-disable-next-line react-refresh/only-export-components -- siehe Begruendung bei resolveLeftSectionKind
export function belowLimitInputProps(hasBelowLimitField: boolean) {
  return hasBelowLimitField
    ? { leftSectionWidth: BELOW_LIMIT_TOGGLE_WIDTH, leftSectionPointerEvents: 'all' as const }
    : {};
}

interface NumberFieldProps extends Omit<NumberInputProps, 'value' | 'defaultValue' | 'onChange' | 'error' | 'form'> {
  form: AnyForm;
  field: string;
  /** Decimal fields are stored as strings (DECIMAL columns); integers as numbers. */
  kind: 'decimal' | 'integer';
  align?: 'left' | 'right';
  /**
   * Nicht-blockierender Hinweis (D-09), ausdruecklich vom aufrufenden Abschnitt gesetzt.
   * Optional: ohne ihn holt sich die Komponente den Hinweis zu `field` selbst aus
   * `FieldWarningsContext` (Plan 20-16).
   */
  warning?: string;
  /**
   * Einheit (Phase 29, `unitLabel.tsx`), aus der `withUnit`/`unitSuffix` sowohl den
   * Label-Zusatz als auch den Eingabe-Suffix erzeugen -- eine Angabe statt der bisher
   * doppelt gepflegten `label`-Klammer plus `suffix`. Additiv: ohne `unit` verhaelt sich
   * die Komponente wie zuvor, `label`/`suffix` gehen unveraendert durch.
   */
  unit?: string;
  /**
   * Opt-in Bestimmungsgrenzen-Umschalter (D-07/D-08): Pfad des booleschen Gegenstuecks-Felds.
   * Gesetzt, zeigt das Feld links einen "=" / "<"-Umschalter statt (bzw. mit Vorrang vor, D-20)
   * dem generischen `leftSection`/Fehlersymbol. Ohne diesen Prop verhaelt sich die Komponente
   * exakt wie zuvor -- die Erweiterung ist additiv fuer alle uebrigen Verwender.
   */
  belowLimitField?: string;
  /** Beschriftung fuer die Registry (D-13), uebersteuert `label`/`aria-label`; nicht an Mantine. */
  fieldLabel?: string;
}

export function ValidatedNumberInput({
  form,
  field,
  kind,
  align,
  warning,
  unit,
  label,
  suffix,
  leftSection,
  styles,
  maw,
  belowLimitField,
  fieldLabel,
  ...rest
}: NumberFieldProps) {
  // Rot vor Gelb: liegt ein Fehler an, wird der Hinweis ignoriert (siehe fieldMessage).
  const { message, severity, isError } = useFieldMessage(
    form,
    field,
    warning,
    registryLabel(fieldLabel, label, rest['aria-label'])
  );
  const raw = getPath(form.getValues(), field) as string | number | null | undefined;
  const defaultValue =
    kind === 'decimal'
      ? raw !== null && raw !== undefined && raw !== '' ? parseFloat(String(raw)) : ''
      : raw === null || raw === undefined || raw === '' ? '' : Number(raw);
  const composedLabel = withUnit(label, unit);
  const composedSuffix = unit ? unitSuffix(unit) : suffix;

  return (
    <FieldMessageTooltip
      message={message}
      severity={severity}
      // `maw` unveraendert an den Vorwert durchgereicht (Befund 24-09): ohne ihn stretcht
      // sich die hoerende Box aus `FieldMessageTooltip` in Einspalten-Listen (Stack,
      // `align-items: stretch`) auf die volle Stack-Breite -- unabhaengig vom `maw` DIESES
      // Feldes -- und der Vorwert rechtsbuendigt sich weit rechts vom Feld statt darunter.
      // Siehe `PreviousValueLine.tsx` fuer den vollen Befund samt verworfener Alternative.
      below={<PreviousValueForField field={field} maw={maw} />}
    >
      <NumberInput
        {...rest}
        {...belowLimitInputProps(Boolean(belowLimitField))}
        maw={maw}
        key={form.key(field)}
        defaultValue={defaultValue}
        onChange={(val) => {
          // Ein unfertiger Zwischenzustand wie ein einzelnes "-" ist keine gueltige
          // Zahl. Solche Werte duerfen NICHT in den Formularstate gelangen:
          // - kind="integer": Number("-") === NaN, und die Sync-Diff-Pruefung
          //   (NaN !== NaN ist immer true) wuerde eine Endlos-setValues-Schleife
          //   ausloesen.
          // - kind="decimal": String("-") = "-" landet sonst im Save-Payload und
          //   ergaebe beim Remount defaultValue = parseFloat("-") = NaN.
          // Beide Faelle daher -> null (Guard symmetrisch fuer beide kinds).
          let next: string | number | null;
          if (val === '' || val === null) {
            next = null;
          } else if (kind === 'decimal') {
            // Punkt 8 vom 14.09.2026: `String(val)` allein kippt bei Kleinstwerten in
            // Exponentenschreibweise (`5e-7`), seit `EMISSION_INPUT_PROPS.decimalScale: 7`
            // erreichbar. `toDecimalString()` liefert stattdessen die Dezimalzeichenkette --
            // siehe decimalString.ts.
            next = Number.isNaN(parseFloat(String(val))) ? null : toDecimalString(val, rest.decimalScale);
          } else {
            const intVal = Number(val);
            next = Number.isNaN(intVal) ? null : intVal;
          }
          // NO_REMOUNT (forceUpdate:false) unterdrueckt den per-Feld-Key-Bump -> kein Remount/
          // Fokusverlust beim Tippen. onValuesChange feuert dennoch (Mantine ruft es bedingungslos
          // in setValues) und speist validityStore / valuesVersionStore / useReportCalc -- die
          // komplette Live-Kette (Submit-Gate, FormularD-Live-Summen, Wasser-/Unfall-/AB-C-Vorschau).
          // Der Dirty-Flip-Re-Render aus setCalculatedFieldDirty ist dabei redundant: onValuesChange
          // -> setCalcKey rendert ReportEditPage ohnehin schon pro Tastendruck.
          form.setFieldValue(field, next, NO_REMOUNT);
        }}
        error={isError}
        // D-20: der Umschalter hat Vorrang vor dem Fehlersymbol -- waehrend ein Fehler ansteht,
        // ist der Umschalter genau das Bedienelement, mit dem der Melder ihn beheben soll. Der
        // Fehler selbst bleibt trotzdem sichtbar, unabhaengig vom leftSection-Slot: ueber
        // `error={isError}` (Rahmen-/Flaechenfaerbung, siehe oben) und `messageInputStyles`
        // (Zeile darunter) sowie den umschliessenden `FieldMessageTooltip`. Fuer alle Verwender
        // ohne belowLimitField ist das Ergebnis Zeile fuer Zeile identisch zum bisherigen Stand
        // (D-08 Opt-in): resolveLeftSectionKind liefert dann 'message-icon' oder 'passthrough'.
        leftSection={
          resolveLeftSectionKind(Boolean(belowLimitField), Boolean(message)) === 'below-limit-toggle'
            ? <BelowLimitToggle form={form} field={belowLimitField as string} disabled={rest.disabled} />
            : message
              ? messageIcon(severity)
              : leftSection
        }
        styles={mergeInputStyles(messageInputStyles(severity, align), styles)}
        label={composedLabel}
        suffix={composedSuffix}
      />
    </FieldMessageTooltip>
  );
}

interface TextFieldProps extends Omit<TextInputProps, 'value' | 'defaultValue' | 'onChange' | 'error' | 'form'> {
  form: AnyForm;
  field: string;
  /**
   * Nicht-blockierender Hinweis (D-09), ausdruecklich vom aufrufenden Abschnitt gesetzt.
   * Optional: ohne ihn holt sich die Komponente den Hinweis zu `field` selbst aus
   * `FieldWarningsContext` (Plan 20-16).
   */
  warning?: string;
  /** Beschriftung fuer die Registry (D-13), uebersteuert `label`/`aria-label`; nicht an Mantine. */
  fieldLabel?: string;
}

export function ValidatedTextInput({ form, field, warning, leftSection, styles, fieldLabel, ...rest }: TextFieldProps) {
  // Rot vor Gelb: liegt ein Fehler an, wird der Hinweis ignoriert (siehe fieldMessage).
  const { message, severity, isError } = useFieldMessage(form, field, warning, registryLabel(fieldLabel, rest.label, rest['aria-label']));
  const raw = getPath(form.getValues(), field) as string | null | undefined;

  return (
    <FieldMessageTooltip message={message} severity={severity}>
      <TextInput
        {...rest}
        key={form.key(field)}
        defaultValue={raw ?? ''}
        onChange={(event) => form.setFieldValue(field, event.currentTarget.value || null, NO_REMOUNT)}
        error={isError}
        leftSection={message ? messageIcon(severity) : leftSection}
        styles={mergeInputStyles(messageInputStyles(severity), styles)}
      />
    </FieldMessageTooltip>
  );
}

interface TextareaFieldProps extends Omit<TextareaProps, 'value' | 'defaultValue' | 'onChange' | 'error' | 'form'> {
  form: AnyForm;
  field: string;
  /**
   * Nicht-blockierender Hinweis (D-09), ausdruecklich vom aufrufenden Abschnitt gesetzt.
   * Optional: ohne ihn holt sich die Komponente den Hinweis zu `field` selbst aus
   * `FieldWarningsContext` (Plan 20-16).
   */
  warning?: string;
  /** Beschriftung fuer die Registry (D-13), uebersteuert `label`/`aria-label`; nicht an Mantine. */
  fieldLabel?: string;
}

export function ValidatedTextarea({ form, field, warning, styles, fieldLabel, ...rest }: TextareaFieldProps) {
  // Rot vor Gelb: liegt ein Fehler an, wird der Hinweis ignoriert (siehe fieldMessage).
  const { message, severity, isError } = useFieldMessage(form, field, warning, registryLabel(fieldLabel, rest.label, rest['aria-label']));
  const raw = getPath(form.getValues(), field) as string | null | undefined;

  return (
    <FieldMessageTooltip message={message} severity={severity}>
      <Textarea
        {...rest}
        key={form.key(field)}
        defaultValue={raw ?? ''}
        onChange={(event) => form.setFieldValue(field, event.currentTarget.value || null, NO_REMOUNT)}
        error={isError}
        styles={mergeInputStyles(messageInputStyles(severity), styles)}
      />
    </FieldMessageTooltip>
  );
}

interface SelectFieldProps extends Omit<SelectProps, 'value' | 'defaultValue' | 'onChange' | 'error' | 'form'> {
  form: AnyForm;
  field: string;
  /** Map the Select's string value to the stored value (e.g. boolean) and back. */
  toStored?: (value: string | null) => unknown;
  fromStored?: (stored: unknown) => string | null;
  /**
   * Nicht-blockierender Hinweis (D-09), ausdruecklich vom aufrufenden Abschnitt gesetzt.
   * Optional: ohne ihn holt sich die Komponente den Hinweis zu `field` selbst aus
   * `FieldWarningsContext` (Plan 20-16).
   */
  warning?: string;
  /** Beschriftung fuer die Registry (D-13), uebersteuert `label`/`aria-label`; nicht an Mantine. */
  fieldLabel?: string;
}

export function ValidatedSelect({ form, field, toStored, fromStored, warning, leftSection, styles, fieldLabel, ...rest }: SelectFieldProps) {
  // Rot vor Gelb: liegt ein Fehler an, wird der Hinweis ignoriert (siehe fieldMessage).
  const { message, severity, isError } = useFieldMessage(form, field, warning, registryLabel(fieldLabel, rest.label, rest['aria-label']));
  const stored = getPath(form.getValues(), field);
  const defaultValue = fromStored ? fromStored(stored) : (stored as string | null);

  return (
    <FieldMessageTooltip message={message} severity={severity}>
      <Select
        {...rest}
        key={form.key(field)}
        defaultValue={defaultValue}
        onChange={(value) =>
          form.setFieldValue(field, toStored ? toStored(value) : value, NO_REMOUNT)
        }
        error={isError}
        leftSection={message ? messageIcon(severity) : leftSection}
        styles={mergeInputStyles(messageInputStyles(severity), styles)}
      />
    </FieldMessageTooltip>
  );
}

interface AvvFieldProps {
  form: AnyForm;
  field: string;
  disabled?: boolean;
  /**
   * Nicht-blockierender Hinweis (D-09), ausdruecklich vom aufrufenden Abschnitt gesetzt.
   * Optional: ohne ihn holt sich die Komponente den Hinweis zu `field` selbst aus
   * `FieldWarningsContext` (Plan 20-16).
   */
  warning?: string;
  /** Von anderen Zeilen bereits vergebene Schluessel, D-19 (Plan 26-06 fuellt sie). */
  usedCodes?: ReadonlyMap<string, WasteCodeUsage>;
  /** Rueckmeldung nach jeder Auswahl/Loeschung, damit der Abschnitt seine `usedCodes`-Map neu baut. */
  onCodeChange?: (field: string, code: string | null) => void;
}

/**
 * Renderhilfe fuer eine einzelne Option im AVV-Dropdown -- keine eigene Komponente, nur ein
 * lesbarer Ausschnitt aus `ValidatedAvvSelect`. Code fett (der `*` gefaehrlicher Schluessel ist
 * Teil des amtlichen Codes -- kein zusaetzlicher Marker, erklaert wird er im Dropdown-Fuss) +
 * Titel klein, umbrechend.
 *
 * Der Gruppenkontext steht nicht mehr an jeder Zeile, sondern einmal ueber der Folge
 * (`Combobox.Group`, siehe unten) -- eine zweite Zeile bekommt die Option nur noch im
 * Sperrfall (D-19), wo sie die Fundstelle nennt.
 */
function renderAvvOption(item: AvvItem, usage: WasteCodeUsage | undefined, isSelected: boolean) {
  const blocked = !!usage;

  return (
    <Combobox.Option
      value={item.code}
      key={item.code}
      disabled={blocked}
      active={isSelected}
      className={avvClasses.option}
    >
      {/*
        `align="flex-start"`, damit die Nummer oben stehen bleibt, wenn der Titel
        umbricht -- die AVV-Titel gehen bis 184 Zeichen und waren einzeilig
        abgeschnitten praktisch nicht lesbar. Kein `truncate` mehr am Titel: die
        Option darf mehrzeilig werden, die Liste scrollt ohnehin.
      */}
      <Group gap={6} wrap="nowrap" align="flex-start">
        <Text component="span" size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {item.code}
        </Text>
        <Text component="span" size="sm" className={avvClasses.title} style={{ minWidth: 0 }}>
          {item.title}
        </Text>
      </Group>
      {blocked && (
        <Text size="xs" truncate className={avvClasses.context}>
          {`bereits vergeben in ${usage!.label}`}
        </Text>
      )}
    </Combobox.Option>
  );
}

/**
 * Fuenfte `Validated*`-Komponente (D-23): dasselbe Hinweis-/Faerbungsgeruest wie die vier
 * bestehenden, aber auf Mantines `Combobox`-Compound statt eines nativen `<input>` -- nur so
 * sind der Trefferzaehler im Dropdown-Kopf (D-02) und die Teilformatierung des geschlossenen
 * Felds (D-11, Nummer fett + Titel klein) erreichbar (Select kennt weder `header` noch
 * beliebiges Markup im Feld selbst).
 */
export function ValidatedAvvSelect({ form, field, disabled, warning, usedCodes, onCodeChange }: AvvFieldProps) {
  const { message, severity, isError } = useFieldMessage(form, field, warning);
  const stored = getPath(form.getValues(), field) as string | null | undefined;

  // Das geschlossene Feld ist kein natives `<input>`, sondern gerendertes Markup -- anders als
  // bei den vier Bestandskomponenten reicht `defaultValue` hier nicht, und der forceUpdate-Guard
  // in `commit()` (siehe unten) loest bewusst kein Re-Render aus. Lokaler Zustand, synchronisiert
  // am Mantine-Formularschluessel (Reacts "State beim Prop-Wechsel anpassen"), ersetzt den
  // `key`-Bump der anderen Komponenten -- ein Remount des ganzen Feldes wuerde ein offenes
  // Dropdown schliessen.
  const [selected, setSelected] = useState<string | null>(stored ?? null);
  const formKey = form.key(field);
  const lastKey = useRef(formKey);
  if (lastKey.current !== formKey) {
    lastKey.current = formKey;
    setSelected(stored ?? null);
  }

  const [search, setSearch] = useState('');

  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setSearch('');
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
      combobox.updateSelectedOptionIndex('active', { scrollIntoView: true });
    },
  });

  const commit = (code: string | null) => {
    setSelected(code);
    form.setFieldValue(field, code, NO_REMOUNT);
    onCodeChange?.(field, code);
  };

  const selectedItem = selected ? AVV_BY_CODE.get(selected) : undefined;
  const result = avvVisibleItems(search, AVV_LIMIT);

  return (
    <Combobox
      store={combobox}
      withinPortal
      position="bottom-start"
      // Breiter als die Tabellenspalte (der Dropdown haengt im Portal, die Spalte bleibt
      // gedeckelt); die Klammer haelt ihn auf schmalen Fenstern im Viewport.
      width="min(620px, calc(100vw - 32px))"
      shadow="md"
      onOptionSubmit={(code) => {
        commit(code);
        combobox.closeDropdown();
      }}
    >
      <FieldMessageTooltip message={message} severity={severity}>
        <Combobox.Target targetType="button">
          <InputBase
            component="button"
            type="button"
            pointer
            disabled={disabled}
            error={isError}
            leftSection={message ? messageIcon(severity) : undefined}
            rightSection={
              selected && !disabled ? (
                <Combobox.ClearButton
                  onClear={() => {
                    commit(null);
                    combobox.closeDropdown();
                  }}
                  aria-label="Auswahl entfernen"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={selected && !disabled ? 'all' : 'none'}
            styles={mergeInputStyles(messageInputStyles(severity), { input: { overflow: 'hidden' } })}
            onClick={() => combobox.toggleDropdown()}
          >
            {selected ? (
              <Group gap={6} wrap="nowrap" style={{ overflow: 'hidden' }}>
                <Text
                  component="span"
                  size="sm"
                  fw={600}
                  style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                >
                  {selected}
                </Text>
                {/*
                  `minWidth: 0` gehoert zum `truncate`: die automatische Mindestgroesse eines
                  Flex-Kindes ist sonst `min-content`, und bei `white-space: nowrap` ist das die
                  volle Textbreite -- der Titel koennte nie schrumpfen und wuerde stattdessen die
                  Tabellenspalte aufblaehen (D-11/D-12).
                */}
                <Text component="span" size="xs" c="dimmed" truncate style={{ minWidth: 0 }}>
                  {selectedItem?.title}
                </Text>
              </Group>
            ) : (
              <Input.Placeholder>Abfallschlüssel wählen…</Input.Placeholder>
            )}
          </InputBase>
        </Combobox.Target>
      </FieldMessageTooltip>

      <Combobox.Dropdown>
        <Combobox.Header>
          <Group justify="space-between" wrap="nowrap">
            <Text size="xs" c="dimmed">
              Abfallverzeichnis-Verordnung
            </Text>
            <Text size="xs" c="dimmed">
              {avvHitLabel(result)}
            </Text>
          </Group>
        </Combobox.Header>
        <Combobox.Search
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
            combobox.resetSelectedOption();
          }}
          placeholder="Nummer oder Begriff"
        />
        {result.total === 0 ? (
          <Combobox.Empty>Kein Schlüssel gefunden. Nummern gehen auch ohne Leerzeichen, z. B. 1912.</Combobox.Empty>
        ) : (
          <Combobox.Options mah={320} style={{ overflowY: 'auto' }}>
            {/*
              Gruppiert wird hier NICHT umsortiert: `avvGroupRuns` fasst nur aufeinanderfolgende
              Treffer derselben Gruppe zusammen, die Reihenfolge nach Treffergüte bleibt. Der
              Gruppentitel steht damit einmal ueber seiner Folge statt an jeder Zeile.
              Bewusst ohne `position: sticky` -- ein mitlaufendes Label verdeckt beim
              Pfeiltasten-Navigieren den obersten Eintrag.

              Die Tastaturnavigation stoert die Verschachtelung nicht: `useCombobox` sammelt die
              Eintraege ueber `querySelectorAll('[data-combobox-option]')` auf der ganzen Liste,
              nicht ueber direkte Kinder.

              Keine je-Brennstoffart-Vorschlagsgruppe ueber der Gesamtliste -- D-01 grenzt diese
              Phase ausdruecklich auf die Gesamtliste mit Suche ein. Eine spaetere
              Vorschlagsgruppe waere hier als zusaetzliche Ebene ueber derselben Liste
              einzusetzen und aendert nichts an Suche, Eindeutigkeit oder Validierung.
            */}
            {avvGroupRuns(result.visible).map((run, runIndex) => (
              // Dieselbe Gruppe kann in getrennten Folgen auftreten -- der Code allein
              // waere als React-Key nicht eindeutig.
              <Combobox.Group
                key={`${run.groupCode}-${runIndex}`}
                label={run.title}
                classNames={{ groupLabel: avvClasses.groupLabel }}
              >
                {run.items.map((item) => {
                  const usage = usedCodes?.get(item.code);
                  const blocked = usage && usage.field !== field ? usage : undefined;
                  return renderAvvOption(item, blocked, item.code === selected);
                })}
              </Combobox.Group>
            ))}
          </Combobox.Options>
        )}
        {/*
          Der `*` ist Teil des amtlichen Schluessels und steht ohne Erklaerung in der Luft --
          der Fuss benennt ihn einmal fuer die ganze Liste (statt eines zweiten, roten
          Markers je Zeile, der wie eine Pflichtfeldkennzeichnung aussah).
        */}
        <Combobox.Footer>
          <Text size="xs" c="dimmed">
            Ein{' '}
            <Text component="span" fw={600} inherit>
              *
            </Text>{' '}
            hinter der Nummer kennzeichnet einen gefährlichen Abfall.
          </Text>
        </Combobox.Footer>
      </Combobox.Dropdown>
    </Combobox>
  );
}
